/**
 * DuplicateCustomersTool · v2.7.65+E247
 * =====================================
 * Admin → Riders tab helper. Scans db.customers for records that share the
 * same phone number (the classic "four Jane Smith rows" problem) and lets an
 * operator merge each group into a single survivor in one click. The backend
 * auto-picks the best survivor (most rides on record → most complete profile →
 * oldest) and re-points every ride / booking / card / session to it before
 * deleting the extras. A one-step confirm guards the destructive action.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Loader2, GitMerge, AlertTriangle, Users, ChevronDown, ChevronUp, RefreshCw, CheckCircle2 } from "lucide-react";

const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "—";
};

export default function DuplicateCustomersTool({ onMerged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmKey, setConfirmKey] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/customers/duplicates");
      setGroups(data?.groups || []);
    } catch {
      toast.error("Could not scan for duplicate customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { scan(); }, [scan]);

  const mergeGroup = async (group) => {
    const primaryId = group.suggested_primary_id;
    const dupIds = (group.records || []).map((r) => r.id).filter((id) => id && id !== primaryId);
    if (!primaryId || dupIds.length === 0) return;
    setBusyKey(group.phone);
    try {
      const { data } = await adminApi.post("/admin/customers/merge", {
        primary_id: primaryId,
        duplicate_ids: dupIds,
      });
      const n = data?.merged_count || dupIds.length;
      toast.success(`Merged ${n} duplicate${n === 1 ? "" : "s"} for ${group.display_name} into one record`);
      setGroups((prev) => prev.filter((g) => g.phone !== group.phone));
      setConfirmKey(null);
      onMerged?.();
    } catch {
      toast.error("Merge failed — please try again");
    } finally {
      setBusyKey(null);
    }
  };

  const total = groups.length;

  return (
    <Card className="bg-zinc-950 border border-amber-400/30 mb-3" data-testid="duplicate-customers-tool">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="dup-tool-toggle"
      >
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-amber-400" />
          <span className="font-bold text-zinc-100 text-sm">Duplicate Customers</span>
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />
          ) : total > 0 ? (
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="dup-tool-count">
              {total} group{total === 1 ? "" : "s"} to clean up
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1" data-testid="dup-tool-clean">
              <CheckCircle2 className="h-3 w-3" />All clean
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-800 pt-3" data-testid="dup-tool-body">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-zinc-400">
              Records that share one phone number. Merging keeps the strongest record (most rides → most complete → oldest) and moves every ride, booking and card onto it.
            </p>
            <Button size="sm" variant="outline" onClick={scan} disabled={loading} className="bg-zinc-950 border-zinc-700 text-zinc-300 h-7 text-xs shrink-0 ml-3" data-testid="dup-scan-btn">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />Rescan
            </Button>
          </div>

          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-zinc-500" /></div>
          ) : total === 0 ? (
            <div className="py-6 text-center text-sm text-zinc-500" data-testid="dup-empty">No duplicate customer records found.</div>
          ) : (
            groups.map((g) => {
              const confirming = confirmKey === g.phone;
              const busy = busyKey === g.phone;
              return (
                <div key={g.phone} className="rounded-lg border border-zinc-800 bg-zinc-900 p-3" data-testid={`dup-group-${g.phone}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                      <div>
                        <div className="text-sm font-bold text-zinc-100">{g.display_name}</div>
                        <div className="text-[11px] text-zinc-400">{fmtPhone(g.phone)} · {g.count} records</div>
                      </div>
                    </div>
                    {confirming ? (
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" onClick={() => mergeGroup(g)} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-7 text-xs" data-testid={`dup-merge-confirm-${g.phone}`}>
                          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <GitMerge className="h-3 w-3 mr-1" />}
                          Confirm · merge {g.count} into 1
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)} disabled={busy} className="text-zinc-400 hover:text-zinc-200 h-7 text-xs" data-testid={`dup-merge-cancel-${g.phone}`}>Cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setConfirmKey(g.phone)} className="bg-zinc-950 border-amber-400/50 text-amber-300 hover:bg-amber-400/10 h-7 text-xs" data-testid={`dup-merge-${g.phone}`}>
                        <GitMerge className="h-3 w-3 mr-1" />Merge into one
                      </Button>
                    )}
                  </div>
                  {confirming && (
                    <div className="mt-2 text-[11px] text-amber-300/90 bg-amber-400/10 border border-amber-400/25 rounded px-2 py-1">
                      This permanently deletes the {g.count - 1} extra record{g.count - 1 === 1 ? "" : "s"} after moving their rides/bookings onto the survivor. This can&apos;t be undone.
                    </div>
                  )}
                  <div className="mt-2 space-y-1">
                    {(g.records || []).map((r) => {
                      const isPrimary = r.id === g.suggested_primary_id;
                      return (
                        <div key={r.id} className={`flex items-center justify-between gap-2 text-[11px] rounded px-2 py-1 ${isPrimary ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-zinc-950 border border-zinc-800"}`} data-testid={`dup-record-${r.id}`}>
                          <div className="truncate">
                            <span className="text-zinc-200 font-semibold">{r.name || "—"}</span>
                            <span className="text-zinc-500"> · {r.email || "no email"}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-zinc-500">{r.rides_count || 0} ride{(r.rides_count || 0) === 1 ? "" : "s"}</span>
                            {isPrimary && <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300">Keep</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </Card>
  );
}
